import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import getClasses from "utils/getClasses";
import styles from "./styles.module.scss";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const thisMonthIndex = new Date().getMonth();
const thisMonth = monthNames[thisMonthIndex];
const thisYear = new Date().getFullYear();

const getChartData = (data) => {
  const invoiceArr = data?.invoice_amount_months;
  const superbillArr = data?.superbill_amount_months;

  let chartData = [] as any;

  for (let i = 0; i < invoiceArr?.length; i++) {
    for (let j = 0; j < superbillArr?.length; j++) {
      const currInvoice = invoiceArr[i];
      const startPartsInvoice = currInvoice.start_date.split("-");
      const monthInvoice = monthNames[startPartsInvoice[1] - 1];
      const invoices = currInvoice.count;

      const currSuperbill = superbillArr[j];
      const startPartsSuperbill = currSuperbill.start_date.split("-");
      const monthSuperbill = monthNames[startPartsSuperbill[1] - 1];
      const superbills = currSuperbill.count;
      monthInvoice === monthSuperbill &&
        chartData.push({
          month: monthInvoice,
          [BILLING_TYPES.invoices]: invoices,
          [BILLING_TYPES.superbills]: superbills,
        });
    }
  }
  return chartData;
};

/**
 * billing types, also used as data key in chart
 */
const BILLING_TYPES = Object.freeze({
  invoices: "invoices",
  superbills: "superbills",
});

const TOOLTIP_OFFSET = 2;

const BarChart = ({ data, year }) => {
  const chartData = getChartData(data);

  const isFuture = (month) =>
    year >= thisYear && monthNames.indexOf(month) > thisMonthIndex;

  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;
    const isCurr = year === thisYear && payload.value === thisMonth;
    return (
      <text
        x={x}
        y={y}
        dominantBaseline={"hanging"}
        textAnchor="middle"
        fill={
          isCurr
            ? "var(--darker)"
            : !isFuture(payload.value)
            ? "var(--gray01)"
            : "var(--gray02)"
        }
        className={getClasses(
          styles["custom-x-axis-tick"],
          isCurr && styles["custom-x-axis-tick--current"],
        )}
      >
        {payload.value}
      </text>
    );
  };

  const CustomYAxisTick = (props) => {
    const { y, payload } = props;
    return (
      <g transform={`translate(${0},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="start"
          dominantBaseline="middle"
          fill="var(--gray01)"
          className={styles["custom-y-axis-tick"]}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  const RoundedBar = ({ x, y, width, height, fill, value, payload, type }: any) => {
    return (
      <OverlayTrigger
        placement={"top"}
        overlay={
          <Tooltip
            id="active-dot-tooltip"
            className={styles["custom-active-dot__tooltip"]}
          >
            {value}
          </Tooltip>
        }
      >
        <g transform={`translate(${x}, ${y})`}>
          <rect
            {...{ width, fill }}
            height={height || 1}
            rx="4"
            stroke="none"
            className={getClasses(
              styles[`rounded-bar-${type}`],
              isFuture(payload.month) && styles[`rounded-bar-${type}--future`],
            )}
          />
          {/* tooltip target */}
          <rect
            width={width}
            // for tooltip offset
            y={-TOOLTIP_OFFSET}
            // stacked on top of the bar
            height={TOOLTIP_OFFSET}
            fill="none"
          />
        </g>
      </OverlayTrigger>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={"100%"}>
      <ComposedChart
        data={chartData}
        margin={{
          top: 5,
          left: 10,
        }}
        barGap={0}
      >
        <XAxis
          dataKey={"month"}
          tickLine={false}
          axisLine={false}
          interval={0}
          tick={<CustomXAxisTick />}
        />

        <YAxis
          tickLine={false}
          axisLine={false}
          width={40}
          tick={<CustomYAxisTick />}
          tickCount={5}
          domain={!chartData.length ? [0, 100] : [0, "auto"]}
        />
        {/* need this to keep chart interactive */}
        <RechartsTooltip content={() => null} cursor={false} />
        {Object.keys(BILLING_TYPES).map((key) => (
          <Bar
            key={key}
            dataKey={key}
            type={key}
            barSize={20}
            shape={RoundedBar as any}
          />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default BarChart;
